(ns hai.web-app.mobile
  "Shared mobile shell: sticky header (brand + date pill), drawer, FAB+sheet,
   and the add-menu modals the FAB tiles open. Wraps any page body when the
   viewport is below the mobile breakpoint."
  (:require
   [hai.home.mobile.add-sheet :as add-sheet]
   [hai.home.mobile.drawer :as drawer]
   [hai.home.mobile.state :as state]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [hai.web-app.connectivity :as connectivity]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]))

(def ^:private chevron-down-icon (icons/mui-icon :outlined :expand_more))

(o/defstyled mobile-header :header
  {:display "flex" :align-items "center" :justify-content "space-between"
   :gap "12px"
   :padding "8px 14px"
   :position "sticky" :top "0"
   :z-index "20"
   ;; Default semi-transparent cream — sits over the neutral mobile-chrome bg.
   ;; `mobile-chrome.sage-bg` overrides this to the warm beige so the sticky
   ;; header matches the sage gradient background.
   :background t/--card-surface-92
   :backdrop-filter "blur(8px)"
   :-webkit-backdrop-filter "blur(8px)"}
  [:.brand
   {:display "inline-flex" :align-items "center" :gap "8px"
    :padding "6px 10px"
    :border "none" :background "transparent"
    :border-radius "10px"
    :cursor "pointer"
    :font-family "inherit"
    :color t/--humble-primary-dark
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--card-forest-06}]
   [:&:focus-visible
    {:outline-width "2px"
     :outline-style "solid"
     :outline-color t/--humble-primary-dark
     :outline-offset "2px"}]]
  [:.brand-mark :inline-flex :items-center :justify-content-center
   [:svg {:width "28px" :height "28px" :border-radius "7px"}]]
  [:.brand-wordmark
   {:font-weight "700" :font-size "17px" :letter-spacing "-0.01em"}]
  [:.brand-chevron
   {:display "inline-flex"
    :transition "transform 0.2s ease"}
   [:&.open {:transform "rotate(180deg)"}]
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [:.pills
   {:display "inline-flex" :align-items "center" :gap "8px"}]
  [:.profile-link
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :color t/--humble-primary-dark}
   [:svg {:width "20px" :height "20px"}]]
  [:.offline-pill
   {:display "inline-flex" :align-items "center"
    :padding "5px 10px"
    :border-radius "10px"
    :background t/--card-warning-12
    :color t/--card-warning-text
    :font-size "11px" :font-weight "600"
    :letter-spacing "-0.005em"
    :box-shadow "0 1px 2px var(--card-warning-12)"}]
  [:.date-pill
   {:display "inline-flex" :align-items "center"
    :padding "6px 12px"
    :border-radius "10px"
    :background t/--card-surface
    :color t/--card-forest
    :font-size "12px" :font-weight "600"
    :letter-spacing "-0.005em"
    :box-shadow "0 1px 2px var(--card-forest-06)"}])

(def ^:private day-abbrev
  ["Sun" "Mon" "Tue" "Wed" "Thu" "Fri" "Sat"])

(def ^:private month-abbrev
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun"
   "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- format-date-pill [d]
  (str (nth day-abbrev (.getDay d)) ", "
       (nth month-abbrev (.getMonth d)) " "
       (.getDate d)))

(defn- header-view []
  (let [open?   @(<s [::state/menu-open?])
        online? @(<s [::connectivity/online?])]
    [mobile-header
     [:button.brand
      {:type "button"
       :aria-label "Open menu"
       :aria-expanded (str open?)
       :on-click #(>e [::state/set-menu-open? (not open?)])}
      [:span.brand-mark [icons/humble-logo-squidge-dark {:width 28 :height 28}]]
      [:span.brand-wordmark "Humble"]
      [:span.brand-chevron {:class (when open? "open")}
       [chevron-down-icon]]]
     [:div.pills
      (when-not online?
        [:span.offline-pill "Offline"])
      [c/Link {:class "profile-link" :href (routing/href :admin-ui/profile)
               :underline "none" :color "gray" :aria-label "My profile"}
       [layout/profile-icon]]
      [:span.date-pill (format-date-pill (js/Date.))]]]))

(o/defstyled mobile-chrome :div
  {:min-height "100vh"
   :background t/--card-surface
   :display "flex" :flex-direction "column"
   ;; Suppress the default translucent overlay mobile webkit paints on every
   ;; tap target. Inherited so it covers descendants.
   :-webkit-tap-highlight-color "transparent"
   ;; Clip horizontal overflow at the shell so wide rows (collection
   ;; pagination, filter bars, etc.) can't push the page sideways. Any
   ;; piece that genuinely needs to be scrollable should handle its own
   ;; horizontal overflow locally.
   :overflow-x "hidden"
   :width "100%"
   :max-width "100vw"}
  ;; Optional sage radial gradient variant; pages opt in via the route's
  ;; `:mobile-chrome-class`. Default chrome stays on the neutral cream so
  ;; card tints stay readable.
  [:&.sage-bg
   {:background "radial-gradient(circle at 30% 20%, var(--card-sage-soft-20) 0%, transparent 55%), radial-gradient(circle at 70% 80%, var(--card-sage-soft-15) 0%, transparent 55%), var(--card-cream)"}
   [mobile-header {:background t/--card-cream-92}]]
  [:>main
   {:flex "1"
    :padding "8px 12px 96px"
    :display "flex" :flex-direction "column"
    :gap "14px"}]
  ([body]
   (let [route-name @(<s :routing/current-route-name)
         chrome-class (:mobile-chrome-class @(<s [:route/data route-name]))]
     [:<>
      {:class chrome-class}
      [header-view]
      [:main body]
      [drawer/drawer]
      [add-sheet/fab+sheet]
      [add-menu/modals]])))
